<template>
  <section id="submit-memory" ref="formSection" class="section form">
    <div class="form__content">
      <div class="form__title">
        <h2 class="section__title" v-reveal>
          {{ title }}
        </h2>

        <RawHtml v-if="description" v-reveal tag="p" :html="description" />
      </div>

      <div
        class="form__form"
        :class="{
          '-light': bg_color === 'light',
          '-dark': bg_color === 'dark',
          '-blue': bg_color === 'blue',
        }"
      >
        <transition name="fade" mode="out-in">
          <div v-if="sent" class="form__confirm">
            <div class="form__confirm-check">
              <Icon id="checkmark" />
            </div>

            <h3 class="form__confirm-title">
              {{ $t('20thAnniversary.confirm') }}
            </h3>

            <button type="button" class="button -right" @click="reload">
              {{ $t('20thAnniversary.submitAnother') }}

              <div class="button__icon">
                <Icon id="arrow" />
              </div>
            </button>
          </div>

          <div v-else-if="sending">
            <Loader style="min-height: 30vh" />
          </div>

          <form v-else @submit.prevent="submit">
            <RawHtml :html="requiredFieldsNotice" class="form__notice" />

            <div class="form__row">
              <div class="form__field" ref="form.first_name">
                <label
                  for="firstName"
                  class="form__label"
                  :class="{
                    '-error': errorFields.first_name,
                  }"
                >
                  {{ $t('20thAnniversary.firstName') }}
                  <span class="form__required">*</span>
                </label>

                <input
                  id="firstName"
                  v-model="form.first_name"
                  name="first_name"
                  type="text"
                  class="form__input"
                />

                <FieldError :errors="validationErrors.first_name" />
              </div>

              <div class="form__field" ref="form.last_name">
                <label
                  for="lastName"
                  class="form__label"
                  :class="{
                    '-error': errorFields.last_name,
                  }"
                >
                  {{ $t('20thAnniversary.lastName') }}
                  <span class="form__required">*</span>
                </label>

                <input
                  id="lastName"
                  v-model="form.last_name"
                  name="last_name"
                  type="text"
                  class="form__input"
                />

                <FieldError :errors="validationErrors.last_name" />
              </div>
            </div>

            <div class="form__field" ref="form.organization">
              <label for="organization" class="form__label">
                {{ $t('20thAnniversary.oganization') }}
              </label>

              <input
                id="organization"
                v-model="form.organization"
                name="organization"
                type="text"
                class="form__input"
              />

              <FieldError :errors="validationErrors.organization" />
            </div>

            <div class="form__field" ref="form.email">
              <label
                for="email"
                class="form__label"
                :class="{
                  '-error': errorFields.email,
                }"
              >
                {{ $t('20thAnniversary.email') }}
                <span class="form__required">*</span>
              </label>

              <input
                id="email"
                v-model="form.email"
                name="email"
                type="email"
                class="form__input"
              />

              <FieldError :errors="validationErrors.email" />
            </div>

            <div class="form__field" ref="form.trip">
              <label for="trip" class="form__label">
                {{ $t('20thAnniversary.trip') }}
              </label>

              <input
                id="trip"
                v-model="form.trip"
                name="trip"
                type="text"
                class="form__input"
                :placeholder="$t('20thAnniversary.destination')"
              />

              <FieldError :errors="validationErrors.trip" />
            </div>

            <div class="form__field" ref="form.memory">
              <label
                for="memory"
                class="form__label"
                :class="{
                  '-error': errorFields.memory,
                }"
              >
                {{ $t('20thAnniversary.souvenir') }}
                <span class="form__required">*</span>
              </label>

              <textarea
                id="memory"
                v-model="form.memory"
                name="memory"
                class="form__input form__textarea"
                :placeholder="$t('20thAnniversary.story')"
              ></textarea>

              <FieldError :errors="validationErrors.memory" />
            </div>

            <div class="form__field" ref="form.photo">
              <label class="form__label">
                {{ $t('20thAnniversary.picture') }}
              </label>

              <p class="form__help">
                {{ $t('20thAnniversary.fileButton') }}
              </p>

              <div class="form__file">
                <button type="button" class="form__file-button" @click="openFile">
                  {{ $t('20thAnniversary.chooseAFile') }}

                  <Icon id="trombonne" />
                </button>

                <input
                  ref="file"
                  id="photo"
                  name="photo"
                  type="file"
                  accept="image/*"
                  class="form__file-input"
                  @change="handleFile"
                />

                <span class="form__file-name">
                  <template v-if="form.photo">
                    {{ form.photo.name }}
                  </template>

                  <template v-else>
                    {{ $t('20thAnniversary.noFile') }}
                  </template>
                </span>
              </div>

              <FieldError :errors="validationErrors.photo" />
            </div>

            <div class="form__field" ref="form.consent">
              <label
                class="form__label"
                :class="{
                  '-error': errorFields.consent,
                }"
              >
                {{ $t('20thAnniversary.publication') }}
                <span class="form__required">*</span>
              </label>

              <input
                id="consent"
                v-model="form.consent"
                name="consent"
                type="checkbox"
                class="form__checkbox-input"
              />

              <label for="consent" class="form__consent">
                <div class="form__checkbox">
                  <Icon v-if="form.consent" id="checkmark" />
                </div>

                <span>
                  {{ $t('20thAnniversary.autorization') }}
                </span>
              </label>

              <FieldError :errors="validationErrors.consent" />
            </div>

            <div class="form__form-submit">
              <button class="button -right" type="submit">
                {{ $t('20thAnniversary.submit') }}

                <div class="button__icon">
                  <Icon id="arrow" />
                </div>
              </button>
            </div>

            <transition name="fade" appear>
              <div v-if="error" class="form__form-error">
                {{ error }}
              </div>
            </transition>
          </form>
        </transition>
      </div>
    </div>
  </section>
</template>

<script>
import Loader from '@/components/blocks/Loader.vue'
import FieldError from '@/components/global/FieldError.vue'
import Icon from '@/components/global/Icon.vue'
import RawHtml from '@/components/global/RawHtml.vue'
import http from '@/extensions/http'

export default {
  name: 'SectionForm',

  components: {
    FieldError,
    Icon,
    Loader,
    RawHtml,
  },

  props: {
    title: {
      type: String,
      default: 'Title',
    },

    description: {
      type: String,
      default: 'Text',
    },

    bg_color: {
      type: String,
      default: 'light',
    },
  },

  data() {
    return {
      sending: false,
      sent: false,
      error: null,
      validationErrors: {},
      errorFields: {},

      form: {
        first_name: '',
        last_name: '',
        organization: '',
        email: '',
        trip: '',
        memory: '',
        photo: null,
        consent: false,
      },
    }
  },

  watch: {
    form: {
      deep: true,

      handler() {
        this.errorFields = {}
      },
    },

    error(to) {
      if (to) {
        setTimeout(() => {
          this.error = false
        }, 5000)
      }
    },
  },

  computed: {
    requiredFieldsNotice() {
      return this.$t('20thAnniversary.requiredFields').replace(
        '*',
        '<span class="form__notice-required">*</span>',
      )
    },
  },

  methods: {
    reload() {
      this.form = {
        first_name: '',
        last_name: '',
        organization: '',
        email: '',
        trip: '',
        memory: '',
        photo: null,
        consent: false,
      }

      this.validationErrors = {}
      this.errorFields = {}
      this.sent = false
      this.sending = false
    },

    openFile() {
      this.$refs.file.click()
    },

    handleFile() {
      this.form.photo = this.$refs.file.files[0]
    },

    submit() {
      if (this.sent || this.sending) {
        return
      }

      const valid = this.validate()

      if (!valid) {
        return
      }

      this.sending = true

      const formData = new FormData()

      Object.entries(this.form).forEach(([key, value]) => {
        if (value !== null && value !== '') {
          if (key === 'consent') {
            formData.append(key, value ? 1 : 0)
          } else {
            formData.append(key, value)
          }
        }
      })

      http
        .post('/memories', formData)
        .then(() => {
          this.sent = true
          this.sending = false

          this.$nextTick(() => {
            const section = this.$refs.formSection

            if (section) {
              this.scrollTo(section)
            }
          })
        })
        .catch((err) => {
          let ref = null

          this.sending = false

          if (err.message && err.message.includes('Network Error')) {
            ref = this.$refs['form.photo']

            this.validationErrors = {
              photo: [this.$t('20thAnniversary.fileTooBigError', { size: '5mb' })],
            }
          } else if (err.response && err.response.data) {
            this.validationErrors = err.response.data.errors ?? {}

            Object.keys(this.validationErrors).forEach((key) => {
              this.errorFields[key] = true

              if (!ref) {
                ref = this.$refs[`form.${key}`]
              }
            })
          }

          if (ref) {
            this.scrollTo(ref)
          }
        })
    },

    scrollTo(ref) {
      const y = ref.getBoundingClientRect().top + window.scrollY - 175

      window.scrollTo({
        top: y,
        behavior: 'smooth',
      })
    },

    validateEmail(email) {
      const pattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

      return pattern.test(email)
    },

    validate() {
      let ref = false

      this.validationErrors = {}
      this.errorFields = {}

      if (!this.form.first_name.length) {
        this.errorFields.first_name = true

        if (!ref) {
          ref = this.$refs['form.first_name']
        }
      }

      if (!this.form.last_name.length) {
        this.errorFields.last_name = true

        if (!ref) {
          ref = this.$refs['form.last_name']
        }
      }

      if (!this.form.email.length) {
        this.errorFields.email = true

        if (!ref) {
          ref = this.$refs['form.email']
        }
      } else if (!this.validateEmail(this.form.email)) {
        this.errorFields.email = true

        this.validationErrors.email = [this.$t('20thAnniversary.invalidEmail')]

        if (!ref) {
          ref = this.$refs['form.email']
        }
      }

      if (!this.form.memory.length) {
        this.errorFields.memory = true

        if (!ref) {
          ref = this.$refs['form.memory']
        }
      }

      if (!this.form.consent) {
        this.errorFields.consent = true

        if (!ref) {
          ref = this.$refs['form.consent']
        }
      }

      if (this.form.photo && this.form.photo.size > 5242880) {
        this.errorFields.photo = true

        this.validationErrors.photo = [this.$t('20thAnniversary.fileTooBigError', { size: '5mb' })]

        if (!ref) {
          ref = this.$refs['form.photo']
        }
      }

      if (ref) {
        this.scrollTo(ref)

        return false
      }

      return true
    },
  },
}
</script>
